import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import Lottie from 'lottie-react';

import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { debug } from '../../utilities/misc';
import { containerStyles } from '../../styles/layout';
import { headers, body } from '../../styles/type';

import Image from '../Image';
import TextRenderer from '../TextRenderer';
import Button from '../buttons/Button';
import ButtonText from '../buttons/ButtonText';
import ButtonGroup from '../ButtonGroup';

const Container = styled.div`
  padding: ${rem(48)} 0;
  overflow: hidden;
  background-color : ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
  `}
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  ${ButtonGroup} {
    justify-content: center;
  }

  ${ButtonText} {
    font-size: ${rem(20)};
    margin: ${rem(12)};
  }
`;

const MediaWrapper = styled.div`
  z-index: 0;
  position: relative;
  display: flex;
  ${({ overlapAsset }) => overlapAsset && `
    margin-top: ${rem(-80)};
  `}
`;

const StyledImage = styled(Image)`
  margin: 0 auto;
`;

const LottieStyled = styled(Lottie)`
  width: 100%;
`;

const Headline = styled.h1`
  ${headers.h2}
  color: ${colors.blue['800']};
  max-width: ${rem(720)};
  margin-bottom: ${rem(16)};

  ${media('>medium')} {
    max-width: ${rem(960)};
  }
`;

const Subhead = styled.p`
  ${body.regular}
  ${headers.h6}
  color: ${colors.blue['500']};
  text-align: center;
  margin-bottom: ${rem(12)};
`;

const TextContent = styled.p`
  ${body.regular}
  color: ${colors.gray['600']};
  max-width: ${rem(720)};
  margin: 0 auto ${rem(32)};
`;

const Disclaimer = styled.div`
  ${body.richText}
  margin-top: ${rem(16)};
  margin-bottom: ${rem(40)};
  color: ${colors.blue['800']};
  a {
    color: #001C33;
    text-decoration: underline;
  }
  a:hover, a:focus {
    text-decoration: none;
  }
`;

const StackedHeader = ({
  subhead,
  headline,
  copy,
  disclaimerCopy,
  ctaText,
  ctaLink,
  secondaryCtaText,
  secondaryCtaLink,
  useH1,
  lottieAnimation,
  overlapAsset,
  image
}) => {
  const [animationData, setAnimationData] = useState(null);
  useEffect(() => {
    let mounted = true;
    const animationDataUrl = lottieAnimation?.file?.url;

    const getAnimationData = async () => {
      if (!animationDataUrl) {
        return;
      }

      try {
        const response = await fetch(animationDataUrl);
        const data = await response.json();

        if (mounted) {
          setAnimationData(data);
        }
      } catch (error) {
        debug('Error fetching Lottie animation data', error);
      }
    };

    getAnimationData();

    return () => {
      mounted = false;
    };
  }, [lottieAnimation]);

  return (
    <Container>
      <ContentWrapper>
        {subhead && (
          <Subhead>
            <TextRenderer text={subhead} />
          </Subhead>
        )}
        {headline && (
          <Headline as={useH1 ? 'h1' : 'h2'}>
            <TextRenderer text={headline} />
          </Headline>
        )}
        {copy && (
          <TextContent>
            <TextRenderer text={copy} />
          </TextContent>
        )}
        <ButtonGroup>
          {ctaText && ctaLink && (
            <Button size="large" to={ctaLink}>
              <TextRenderer text={ctaText} />
            </Button>
          )}
          {secondaryCtaText && secondaryCtaLink && (
            <ButtonText
              to={secondaryCtaLink}
            >
              <TextRenderer text={secondaryCtaText} />
            </ButtonText>
          )}
        </ButtonGroup>
        {disclaimerCopy && (
          <Disclaimer>
            <TextRenderer richText text={disclaimerCopy} />
          </Disclaimer>
        )}
      </ContentWrapper>
      <MediaWrapper overlapAsset={overlapAsset}>
        {image && lottieAnimation === null && (
          <StyledImage fullWidth image={image} loading="eager" />
        )}
        {lottieAnimation?.file?.url && (
          <LottieStyled
            animationData={animationData}
            loop
            autoplay
            rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }}
          />
        )}
      </MediaWrapper>
    </Container>
  );
};

StackedHeader.propTypes = {
  headline: PropTypes.string,
  subhead: PropTypes.string,
  copy: PropTypes.string,
  disclaimerCopy: PropTypes.shape({
    raw: PropTypes.string,
  }),
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
  overlapAsset: PropTypes.bool,
  image: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object
  }),
  // eslint-disable-next-line react/forbid-prop-types
  lottieAnimation: PropTypes.shape({
    file: PropTypes.shape({
      url: PropTypes.string
    })
  }),
  useH1: PropTypes.bool
};

export const query = graphql`
  fragment StackedHeaderFragment on ContentfulStackedHeader {
    id
    __typename
    headline
    subhead
    copy
    disclaimerCopy {
      raw
    }
    ctaText
    ctaLink
    secondaryCtaText
    secondaryCtaLink
    image {
      gatsbyImageData(quality: 75, resizingBehavior: FILL)
      file {
        url
      }
      description
    }
    lottieAnimation {
      file {
        url
      }
    }
    overlapAsset
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default StackedHeader;
